import { IWedding } from '@bridebook/models/source/models/Weddings.types';
import type { IWeddingEvent } from '@bridebook/models/source/models/Weddings/Events.types';
import {
  WebsiteTileVariant,
  WeddingWebsiteTileComponent,
} from '@bridebook/web/components/wedding-website/view-mode/partials/tile-component/wedding-website-tile-component';
import { useViewModeEditMode } from '@bridebook/web/components/wedding-website/view-mode/view-mode.context';
import {
  formatEventDate,
  formatEventTimeRange,
} from '@bridebook/web/lib/events/event-date-formatters';
import { sortEvents } from '@bridebook/web/lib/events/sort-unified-events';
import { isMainEvent, SimplifiedEventForDisplay } from '@bridebook/web/lib/events/types';
import {
  DATE_FORMAT,
  dateFromIDatePicker,
  format,
} from '@bridebook/web/lib/time-date-manipulation/time-date-manipulation';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { useDispatch } from '@bridebook/web/lib/utils';
import { interactedWithWeddingWebsiteAnalyticsButtonClick } from '@bridebook/web/lib/wedding-website/analytics';
import {
  useGetWeddingWebsiteDateFormat,
  WeddingWebsiteDateFormat,
} from '@bridebook/web/lib/wedding-website/hooks/use-get-wedding-website-date-format';
import { Palettes } from '@bridebook/web/lib/wedding-website/types';
import isDateExact from '@bridebook/web/lib/weddings/utils/is-date-exact';
import { groupBy } from 'lodash';
import partition from 'lodash/partition';
import { Trans, useTranslation } from 'next-i18next';
import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { FloatingEditButton } from '@bridebook/web/components/wedding-website/view-mode/partials/floating-edit-button/floating-edit-button';
import { useEditorSyncContext } from '@bridebook/web/components/wedding-website/main/desktop/editor-sync-context';

interface IProps {
  palette: Palettes;
  events: IWeddingEvent[];
  weddingDate: IWedding['date'];
}

const EventWithDescription: FC<SimplifiedEventForDisplay> = ({ description, time, name }) => (
  <WeddingWebsiteTileComponent.ItemGroup>
    <WeddingWebsiteTileComponent.TextHeading>
      {formatEventTimeRange(time, ' - ')}
      {name}
    </WeddingWebsiteTileComponent.TextHeading>
    <WeddingWebsiteTileComponent.Text>{description}</WeddingWebsiteTileComponent.Text>
  </WeddingWebsiteTileComponent.ItemGroup>
);

const DailyEventSet: FC<{ date: string; events: SimplifiedEventForDisplay[] }> = ({
  date,
  events,
}) => {
  const format = useGetWeddingWebsiteDateFormat();
  return (
    <WeddingWebsiteTileComponent.ItemGroup>
      <WeddingWebsiteTileComponent.Title>
        {formatEventDate(date, format === WeddingWebsiteDateFormat.Ordinal)}
      </WeddingWebsiteTileComponent.Title>
      {events.map((ev) => (
        <EventWithDescription {...ev} key={ev.id} />
      ))}
    </WeddingWebsiteTileComponent.ItemGroup>
  );
};

export const WeddingWebsiteSchedule: FC<IProps> = ({ palette, events, weddingDate }) => {
  const dispatch = useDispatch();
  const { registerPreviewRef, triggerDetailsSync } = useEditorSyncContext();
  const tileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    registerPreviewRef('schedule', tileRef.current);
  }, [registerPreviewRef]);

  const handleEditSchedule = useCallback(() => {
    triggerDetailsSync('schedule');
    dispatch(
      interactedWithWeddingWebsiteAnalyticsButtonClick({
        section: 'events',
        element: 'editButton',
      }),
    );
  }, [dispatch, triggerDetailsSync]);
  const { t } = useTranslation('weddingWebsite');
  const isPreviewMode = useViewModeEditMode();
  const hasAnyEvents = Number(events?.length) > 0;

  const [mainEventArr, restOfEventsArr] = partition(events, isMainEvent);
  const mainEvent = mainEventArr?.[0];
  const showAddDatePrompt =
    isPreviewMode && (!weddingDate || !isDateExact(weddingDate) || !mainEvent);

  // Some conversion is needed as the Main Event doesn't have the date field and it needs to be added from the wedding (if available)
  const simplifiedEvents: SimplifiedEventForDisplay[] = useMemo(() => {
    const mainEventWithAllDataArr: SimplifiedEventForDisplay[] = (() => {
      if (!weddingDate || !isDateExact(weddingDate) || !mainEvent) return [];
      const date = dateFromIDatePicker(weddingDate);
      if (!date) return [];
      return [
        {
          ...mainEvent,
          date: format(date, DATE_FORMAT.DATE.ISO),
        },
      ];
    })();

    return sortEvents([...mainEventWithAllDataArr, ...restOfEventsArr]);
  }, [mainEvent, restOfEventsArr, weddingDate]);

  if (!isPreviewMode && !hasAnyEvents) return null;

  const groupedEvents = groupBy(
    simplifiedEvents.filter((evt) => evt.featured),
    (ev) => ev.date,
  );
  const hasVisibleEvents = Object.keys(groupedEvents).length > 0;

  return (
    <div ref={tileRef} className="group w-full">
      <WeddingWebsiteTileComponent
        palette={palette}
        variant={WebsiteTileVariant.Primary}
        title={t('previewMode.tile.schedule.title')}>
        {showAddDatePrompt && (
          <WeddingWebsiteTileComponent.ItemGroup>
            <WeddingWebsiteTileComponent.Text>
              {t('previewMode.tile.schedule.addDatePrompt')}
            </WeddingWebsiteTileComponent.Text>
          </WeddingWebsiteTileComponent.ItemGroup>
        )}
        {hasAnyEvents ? (
          hasVisibleEvents ? (
            Object.entries(groupedEvents).map(([date, eventsAtDate]) => (
              <DailyEventSet key={date} date={date} events={eventsAtDate} />
            ))
          ) : (
            !showAddDatePrompt && (
              <WeddingWebsiteTileComponent.TextPlaceholder>
                {t('previewMode.tile.schedule.dateTBC')}
              </WeddingWebsiteTileComponent.TextPlaceholder>
            )
          )
        ) : (
          <WeddingWebsiteTileComponent.Text>
            <WeddingWebsiteTileComponent.TextPlaceholder>
              <Trans ns="weddingWebsite" i18nKey="previewMode.tile.schedule.placeholder">
                What does the day look like? Help your guests plan for your special day!
                <br />
                <br />
                Add your events timeline here...
              </Trans>
            </WeddingWebsiteTileComponent.TextPlaceholder>
          </WeddingWebsiteTileComponent.Text>
        )}
        <FloatingEditButton onClick={handleEditSchedule} href={UrlHelper.events.main} />
      </WeddingWebsiteTileComponent>
    </div>
  );
};
